<template lang="pug">
  v-card
    v-card-title
      h2.card-title {{ 'modals.edit_fodder' | translate }}
    v-card-content
      v-type-select.mb-3(
        v-model="typeSearchText"
        :label="$t('base.ingredient_type')"
        :hint="errorMessage('ingredient_type')"
        :invalid="!!errorMessage('ingredient_type')"
        :custom-types="filteredCustomTypes"
        :default-types="filteredDefaultTypes"
        @select="onTypeSelect"
        @create-enabled="onCreateTypeEnabled")
      v-text-field.mb-3(
        :label="$t('base.name')"
        :hint="errorMessage('name')"
        :invalid="!!errorMessage('name')"
        @input="clearError('name')"
        v-model="form.name")
      v-select.mb-3(
        :label="$t('base.stock')"
        returnObject
        :options="stocks"
        :hint="errorMessage('stock')"
        :invalid="!!errorMessage('stock')"
        @input="clearError('stock')"
        v-model="form.stock")
      .toogle-wrap
        ui-toggle-unit(
          :tolerancePercent="form.tolerance_percent"
          :toleranceUnit="form.tolerance_unit"
          :toleranceKg="form.tolerance_kg"
          @update="onToleranceUpdate")
      v-switch.mb-3(
        :label="$t('base.ignore_in_reports')"
        v-model="form.ignored_in_report")
      v-switch(
        :label="$t('base.auto_load')"
        v-model="form.auto_load")
    v-card-actions
      v-btn(
        :loading="loading"
        @click="onUpdate")
        | {{ 'base.save' | translate }}
</template>

<script>
import api from '@/api'
import { mapActions, mapGetters } from 'vuex'
import formErrorsMixin from '@/mixins/form-errors.mixin.js'
import UiToggleUnit from '@/components/ui/ui-toggle-unit/UiToggleUnit.vue'

export default {
  name: 'EditFodderModal',

  props: {
    data: {
      type: Object,
      required: true,
      default: null
    }
  },

  components: {
    UiToggleUnit
  },

  mixins: [formErrorsMixin],

  data: () => ({
    form: {
      name: null,
      ingredient_type: null,
      stock: null,
      dry_matter: 0,
      is_animalfeed: true,
      ignored_in_report: false,
      auto_load: false,
      tolerance_kg: 0,
      tolerance_percent: 97,
      tolerance_unit: 'percent'
    },
    errors: {
      name: null,
      stock: null,
      ingredient_type: null
    },
    loading: false,
    typeSearchText: '',
    originalTypeName: '',
    selectedType: null,
    isDefaultType: false,
    createTypeEnabled: false,
    creatingNewType: false
  }),

  computed: {
    ...mapGetters('ingredienttypes', ['ingredientTypes', 'defaultIngredientTypes']),
    ...mapGetters('stocks', ['stocks']),

    filteredCustomTypes () {
      return this.ingredientTypes.filter(type => !type.deleted)
    },

    filteredDefaultTypes () {
      const customNames = this.ingredientTypes
        .filter(type => !type.deleted)
        .map(t => t.name.toLowerCase())

      return this.defaultIngredientTypes.filter(defaultType =>
        !customNames.includes(defaultType.name.toLowerCase())
      )
    }
  },

  async mounted () {
    this.form = {
      ...this.data
    }

    if (this.form.ingredient_type && this.form.ingredient_type.name) {
      this.typeSearchText = this.form.ingredient_type.name
      this.originalTypeName = this.form.ingredient_type.name
      this.selectedType = this.form.ingredient_type
      this.createTypeEnabled = true
    }

    const farm = +this.$route.params.farm_id
    await this.loadIngredientTypes({ farm, limit: 10000 })
    await this.loadDefaultIngredientTypes()

    const company = +this.$route.params.company_id
    this.loadStocks({ company, farm })
  },

  methods: {
    ...mapActions('ingredients', ['updateIngredient', 'loadIngredients']),
    ...mapActions('ingredienttypes', ['loadIngredientTypes', 'loadDefaultIngredientTypes', 'createIngredientType']),
    ...mapActions('stocks', ['loadStocks']),

    onTypeSelect ({ type, isDefault }) {
      this.selectedType = type
      this.isDefaultType = isDefault

      this.form.name = type.name

      this.createTypeEnabled = true
      this.errors.ingredient_type = null
    },

    onCreateTypeEnabled (enabled) {
      this.createTypeEnabled = enabled
      if (!enabled) {
        this.selectedType = null
      }
      this.errors.ingredient_type = null
    },

    onToleranceUpdate (payload) {
      this.form.tolerance_kg = payload.tolerance_kg
      this.form.tolerance_percent = payload.tolerance_percent
      this.form.tolerance_unit = payload.tolerance_unit
    },

    async onUpdate () {
      if (!this.checkValidData()) return

      this.loading = true

      let typeId = null

      if (this.form.ingredient_type && this.form.ingredient_type.id) {
        typeId = this.form.ingredient_type.id
      }

      const typeChanged = this.typeSearchText !== this.originalTypeName

      if (typeChanged) {
        const existingCustomType = this.ingredientTypes.find(t =>
          t.name.toLowerCase() === this.typeSearchText.toLowerCase()
        )

        if (!this.selectedType || this.isDefaultType || !existingCustomType) {
          this.creatingNewType = true
          const typePayload = {
            farm: +this.$route.params.farm_id,
            name: this.typeSearchText
          }
          const typeError = await this.createIngredientType(typePayload)

          if (typeError) {
            this.$toasted.error(this.$t('errors.incorrect_data'))
            this.loading = false
            this.creatingNewType = false
            return
          }

          const farm = +this.$route.params.farm_id
          await this.loadIngredientTypes({ farm, limit: 10000 })

          const newType = this.ingredientTypes.find(t =>
            t.name.toLowerCase() === this.typeSearchText.toLowerCase()
          )

          if (newType) {
            typeId = newType.id
          } else {
            this.$toasted.error(this.$t('errors.type_not_created'))
            this.loading = false
            this.creatingNewType = false
            return
          }

          this.creatingNewType = false
        } else if (this.selectedType) {
          typeId = this.selectedType.id
        }
      }

      const payload = {
        ...this.form,
        ingredient_type: { id: typeId },
        farm: this.$route.params.farm_id
      }

      const { errors } = await api.ingredients.updateById(this.form.id, payload)

      if (!errors) {
        await this.loadIngredients({ farm: this.$route.params.farm_id })
        this.$root.$emit('UpdateFodderTable')
        this.$emit('close')
      } else {
        this.errors = errors

        if (this.errors.non_field_errors) {
          this.errors.ingredient_type = ['5']
        }
      }

      this.loading = false
    },

    checkValidData () {
      let valid = true

      this.errors.name = null
      this.errors.stock = null
      this.errors.ingredient_type = null

      if (!this.form.name) {
        this.errors.name = ['3']
        valid = false
      } else if (this.form.name.length >= 100) {
        this.errors.name = ['6', 100]
        valid = false
      }

      if (!this.form.stock) {
        this.errors.stock = ['3']
        valid = false
      }

      if (!this.typeSearchText) {
        this.errors.ingredient_type = ['3']
        valid = false
      }

      return valid
    }
  }
}
</script>